import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimalCollectionMenu from '../components/AnimalCollectionMenu';
import AnimalExclusionFilters from '../components/AnimalExclusionFilters';
import BulkCollectionActions from '../components/BulkCollectionActions';
import {
  appendExclusionParams,
  useAnimalExclusions
} from '../utils/useAnimalExclusions';
import './HomePage.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const PREFERENCES_KEY = 'faunatrack-home-preferences';
const PAGE_SIZES = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];
const MAX_BULK_SELECTION = 500;
const DEFAULT_PREFERENCES = {
  search: '',
  page: 1,
  pageSize: 100,
  taxClass: '',
  taxOrder: '',
  family: '',
  genus: '',
  advancedOpen: false
};

function loadPreferences() {
  try {
    const data = JSON.parse(localStorage.getItem(PREFERENCES_KEY) || '{}');
    const page = Number(data.page);
    const pageSize = Number(data.pageSize);
    const text = key =>
      typeof data[key] === 'string' ? data[key].slice(0, 120) : '';

    return {
      search: text('search'),
      taxClass: text('taxClass'),
      taxOrder: text('taxOrder'),
      family: text('family'),
      genus: text('genus'),
      page: Number.isSafeInteger(page) && page > 0 ? page : 1,
      pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : 100,
      advancedOpen: data.advancedOpen === true
    };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

function getImageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}/${path.replace(/^\/+/, '')}`;
}

// Show a sliding range of pages, plus the first/last pages and ellipses.
function pageNumbers(page, totalPages) {
  const visible = new Set([1, totalPages]);
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) {
    visible.add(i);
  }
  const sorted = [...visible].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push('ellipsis-' + sorted[i]);
    }
    result.push(sorted[i]);
  }
  return result;
}

function HomePage() {
  const navigate = useNavigate();
  const { exclusions, setExclusion } = useAnimalExclusions();
  const [preferences, setPreferences] = useState(loadPreferences);
  const [debouncedSearch, setDebouncedSearch] = useState(preferences.search);
  const [animals, setAnimals] = useState([]);
  const [pagination, setPagination] = useState({
    page: preferences.page,
    pageSize: preferences.pageSize,
    total: 0,
    totalPages: 1
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [selectionError, setSelectionError] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
    } catch {
      // Preferences still work until the page is reloaded.
    }
  }, [preferences]);

  // Avoid a database request for every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(preferences.search);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [preferences.search]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAnimals() {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          page: String(preferences.page),
          pageSize: String(preferences.pageSize),
          search: debouncedSearch.trim(),
          class: preferences.taxClass.trim(),
          order: preferences.taxOrder.trim(),
          family: preferences.family.trim(),
          genus: preferences.genus.trim()
        });
        appendExclusionParams(params, exclusions);

        const response = await fetch(
          `${API_URL}/animals/paginated?${params.toString()}`,
          { signal: controller.signal }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Unable to retrieve animals.');
        }
        if (!Array.isArray(data.animals) || !data.pagination) {
          throw new Error('Invalid response from animal API.');
        }

        if (!controller.signal.aborted) {
          setAnimals(data.animals);
          setPagination(data.pagination);
          if (data.pagination.page !== preferences.page) {
            setPreferences(previous => ({
              ...previous,
              page: data.pagination.page
            }));
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadAnimals();
    return () => controller.abort();
  }, [
        preferences.page,
        preferences.pageSize,
        preferences.taxClass,
        preferences.taxOrder,
        preferences.family,
        preferences.genus,
        debouncedSearch,
        exclusions
      ]);

  function changePreference(key, value) {
    setPreferences(previous => ({
      ...previous,
      [key]: value,
      page: key === 'page' || key === 'advancedOpen' ? previous.page : 1
    }));
  }

  function changeExclusion(key, checked) {
    setExclusion(key, checked);
    setPreferences(previous => ({ ...previous, page: 1 }));
  }

  function changePage(page) {
    if (page < 1 || page > pagination.totalPages || loading) return;
    setPreferences(previous => ({ ...previous, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function toggleSelectMode() {
    setSelectMode(previous => !previous);
    setSelected(new Set());
    setSelectionError('');
  }

  function toggleAnimal(id) {
    if (!selected.has(id) && selected.size >= MAX_BULK_SELECTION) {
      setSelectionError('Select up to 500 animals at once.');
      return;
    }
    setSelectionError('');
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_BULK_SELECTION) next.add(id);
      return next;
    });
  }

  function selectCurrentPage() {
    const allIds = new Set([...selected, ...animals.map(animal => animal.id)]);
    setSelectionError(allIds.size > MAX_BULK_SELECTION
      ? 'Maximum 500 animals selected.' : '');
    setSelected(previous => {
      const next = new Set(previous);
      for (const animal of animals) {
        if (next.size >= MAX_BULK_SELECTION) break;
        next.add(animal.id);
      }
      return next;
    });
  }

  const selectedIds = useMemo(() => [...selected], [selected]);
  const begin = pagination.total === 0
    ? 0
    : (pagination.page - 1) * pagination.pageSize + 1;
  const end = Math.min(
    pagination.total,
    pagination.page * pagination.pageSize
  );

  return (
    <div className="home-page">
      <h1 className="home-page__title">Explore Animals</h1>

      <section className="home-range-compare" aria-label="Geographic range comparison">
        <div className="home-range-compare__copy">
          <h2>Discover where mammal ranges overlap</h2>
          <p>
            Compare the geographic distributions of two or more species.
            Select the animals you want to compare, then choose Check Intersection.
          </p>
        </div>
        <button
          type="button"
          className="home-range-compare__button"
          onClick={toggleSelectMode}
          aria-pressed={selectMode}
        >
          {selectMode ? 'Exit Comparison Mode' : 'Compare Range Maps'}
          <span aria-hidden="true"> →</span>
        </button>
      </section>

      <div className="home-page__search-row">
        <input
          type="search"
          aria-label="Search animals by common or scientific name"
          placeholder="Search common or scientific name..."
          value={preferences.search}
          maxLength={120}
          onChange={event => changePreference('search', event.target.value)}
        />
        <button
          type="button"
          className="home-page__advanced-toggle"
          aria-expanded={preferences.advancedOpen}
          onClick={() => changePreference(
            'advancedOpen', !preferences.advancedOpen
          )}
        >
          Advanced Search {preferences.advancedOpen ? '−' : '+'}
        </button>
      </div>

      <AnimalExclusionFilters
        exclusions={exclusions}
        onChange={changeExclusion}
      />

      {preferences.advancedOpen && (
        <div className="home-page__advanced">
          <h2>Filter by taxonomy</h2>
          <div className="home-page__advanced-fields">
            {[
              ['taxClass', 'Class', 'e.g. Mammalia'],
              ['taxOrder', 'Order', 'e.g. Carnivora'],
              ['family', 'Family', 'e.g. Canidae'],
              ['genus', 'Genus', 'e.g. Canis']
            ].map(([key, label, placeholder]) => (
              <label key={key}>
                <span>{label}</span>
                <input
                  type="text"
                  value={preferences[key]}
                  placeholder={placeholder}
                  maxLength={120}
                  onChange={event => changePreference(key, event.target.value)}
                />
              </label>
            ))}
          </div>
          <button
            type="button"
            className="home-page__clear-filters"
            onClick={() => setPreferences(previous => ({
              ...previous,
              taxClass: '',
              taxOrder: '',
              family: '',
              genus: '',
              page: 1
            }))}
          >
            Clear taxonomy filters
          </button>
        </div>
      )}

      <div className="home-page__toolbar">
        <span className="home-page__count" aria-live="polite">
          {loading ? 'Loading animals...' :
            `Showing ${begin}–${end} of ${pagination.total} animals`}
        </span>

        <div className="home-page__toolbar-actions">
          <label className="home-page__page-size">
            Per page
            <select
              value={preferences.pageSize}
              onChange={event => changePreference(
                'pageSize', Number(event.target.value)
              )}
            >
              {PAGE_SIZES.map(size => (
                <option value={size} key={size}>{size}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={selectMode ? 'home-page__select-many active' : 'home-page__select-many'}
            onClick={toggleSelectMode}
          >
            {selectMode ? 'Done selecting' : 'Select Many'}
          </button>
        </div>
      </div>

      {selectMode && (
        <>
          <p className="home-range-compare__instructions" role="status">
            Select at least two animals below, then click <strong>Check Intersection</strong>
            to view their range maps together.
          </p>
          <div className="home-page__selection-buttons">
            <button type="button" onClick={selectCurrentPage} disabled={loading}>
              Select this page
            </button>
          </div>
          <BulkCollectionActions
            selectedIds={selectedIds}
            onClear={() => { setSelected(new Set()); setSelectionError(''); }}
          />
          {selectionError && (
            <p role="alert" className="home-error">{selectionError}</p>
          )}
        </>
      )}

      {error && <p className="home-error" role="alert">{error}</p>}

      {!error && !loading && animals.length === 0 && (
        <p>No animals match your search or filters.</p>
      )}

      {!error && !loading && (
        <div className="animal-grid">
          {animals.map(animal => {
            const imageUrl = getImageUrl(animal.image_path);
            const chosen = selected.has(animal.id);
            const name = animal.name || animal.sci_name || 'Unnamed animal';

            return (
              <div
                key={animal.id}
                className={`animal-item${selectMode ? ' animal-item--selectable' : ''}${chosen ? ' animal-item--selected' : ''}`}
                onClick={() => selectMode
                  ? toggleAnimal(animal.id)
                  : navigate(`/animal/${animal.id}`)}
                onKeyDown={event => {
                  if (event.target === event.currentTarget &&
                    (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    if (selectMode) toggleAnimal(animal.id);
                    else navigate(`/animal/${animal.id}`);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={selectMode ? chosen : undefined}
                aria-label={selectMode ? `Select ${name}` : `View ${name}`}
              >
                {selectMode ? (
                  <input
                    type="checkbox"
                    className="animal-item__checkbox"
                    checked={chosen}
                    aria-label={`Select ${name}`}
                    onClick={event => event.stopPropagation()}
                    onChange={() => toggleAnimal(animal.id)}
                  />
                ) : (
                  <AnimalCollectionMenu animalId={animal.id} />
                )}

                {imageUrl ? (
                  <img src={imageUrl} alt={name} loading="lazy" />
                ) : (
                  <div className="animal-card-placeholder">No image available</div>
                )}
                <p>{name}</p>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && pagination.totalPages > 1 && (
        <nav className="home-page__pagination" aria-label="Animal pages">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => changePage(pagination.page - 1)}
          >
            ‹ Previous
          </button>

          {pageNumbers(pagination.page, pagination.totalPages).map(item =>
            typeof item === 'string' ? (
              <span key={item} className="home-page__ellipsis">…</span>
            ) : (
              <button
                type="button"
                key={item}
                className={item === pagination.page ? 'current' : ''}
                aria-current={item === pagination.page ? 'page' : undefined}
                onClick={() => changePage(item)}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => changePage(pagination.page + 1)}
          >
            Next ›
          </button>
        </nav>
      )}
    </div>
  );
}

export default HomePage;
